import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, AlertTriangle, Filter, User, Users, Clock, Store, CheckCircle2, CircleDot } from "lucide-react";
import { listComplaints } from "../../api/complaints";
import { useAuth } from "../../context/AuthContext";
import PageHeader from "../../components/PageHeader";
import { Card, Input, Select, EmptyState, TableSkeleton } from "../../components/ui";
import { StatusChip, PriorityChip } from "../../components/Chips";
import Pagination from "../../components/Pagination";
import { refLabel, formatDate } from "../../lib/records";
import { COMPLAINT_STATUSES, COMPLAINT_PRIORITIES } from "../../lib/enums";

// Vocabularies live in lib/enums.js so the filters here and the chips that
// render the results can never drift apart.
const STATUSES = COMPLAINT_STATUSES;
const PRIORITIES = COMPLAINT_PRIORITIES;

export default function ComplaintsList() {
  const { partner } = useAuth();
  const isChain = partner?.kind === "CHAIN";
  const [searchParams, setSearchParams] = useSearchParams();

  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, pages: 1 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  // Seeded from the URL so the dashboard's status tiles land on a pre-filtered
  // list rather than dropping the user into an unfiltered one.
  const [status, setStatus] = useState(() => {
    const s = searchParams.get("status");
    return STATUSES.includes(s) ? s : "";
  });
  const [priority, setPriority] = useState(() => {
    const p = searchParams.get("priority");
    return PRIORITIES.includes(p) ? p : "";
  });
  const [loading, setLoading] = useState(true);

  // Mirror the active filters back into the URL so the view survives a refresh
  // and stays shareable.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (status) next.set("status", status);
    else next.delete("status");
    if (priority) next.set("priority", priority);
    else next.delete("priority");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, priority]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: 10,
        ...(search ? { search } : {}),
        ...(status ? { status } : {}),
        ...(priority ? { priority } : {}),
      };
      const res = await listComplaints(params);
      setItems(res.data.items);
      setPagination(res.data.pagination);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [page, search, status, priority]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPage(1);
  }, [search, status, priority]);

  const getStatusCount = (statusValue) => {
    return items.filter(item => item.status === statusValue).length;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50">
      <div className="px-4 sm:px-6 py-4 sm:py-6 lg:px-8">
        
        <PageHeader
          title="Complaints"
          subtitle={
            isChain
              ? "Every complaint raised across your branches, newest first."
              : "Track complaints raised against your branch."
          }
        />

        <Card className="bg-white/95 backdrop-blur-sm border border-emerald-200 shadow-2xl shadow-emerald-200/50">
          {/* Filters */}
          <div className="flex flex-col gap-3 border-b border-emerald-200/60 p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <div className="relative flex-1">
                <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <Input
                  placeholder="Search by title…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 border-gray-300 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/30 rounded-xl transition-all duration-200 text-gray-800 placeholder:text-gray-400 bg-white text-sm"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <Select 
                  value={status} 
                  onChange={(e) => setStatus(e.target.value)} 
                  className="flex-1 sm:flex-none sm:w-40 border-gray-300 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/30 rounded-xl transition-all duration-200 text-gray-800 bg-white text-sm"
                >
                  <option value="">All statuses</option>
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s.replace("_", " ")}
                    </option>
                  ))}
                </Select>
                <Select 
                  value={priority} 
                  onChange={(e) => setPriority(e.target.value)} 
                  className="flex-1 sm:flex-none sm:w-36 border-gray-300 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/30 rounded-xl transition-all duration-200 text-gray-800 bg-white text-sm"
                >
                  <option value="">All priorities</option>
                  {PRIORITIES.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
          </div>

          {/* Stats Summary */}
          {!loading && items.length > 0 && (
            <div className="flex flex-wrap items-center gap-3 px-3 sm:px-4 py-2 sm:py-3 bg-emerald-100/40 border-b border-emerald-200/60">
              <span className="text-[10px] sm:text-xs font-semibold text-gray-700 flex items-center gap-1">
                <Filter size={11} className="text-emerald-700" />
                Filtered: <span className="text-gray-900 font-bold">{items.length}</span>
              </span>
              <span className="w-px h-4 bg-emerald-300" />
              <div className="flex flex-wrap items-center gap-2">
                {STATUSES.slice(0, 4).map((s) => {
                  const count = getStatusCount(s);
                  if (count === 0) return null;
                  return (
                    <span key={s} className="text-[10px] sm:text-xs font-medium text-gray-700 flex items-center gap-1">
                      <span className={`w-1.5 h-1.5 rounded-full ${
                        s === 'OPEN' ? 'bg-blue-600' :
                        s === 'ASSIGNED' ? 'bg-amber-500' :
                        s === 'UNDER_REVIEW' ? 'bg-purple-600' :
                        'bg-green-600'
                      }`} />
                      <span className="hidden xs:inline">{s.replace('_', ' ')}:</span>
                      <span className="text-gray-900 font-bold">{count}</span>
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {loading ? (
            <TableSkeleton rows={6} cols={6} />
          ) : items.length === 0 ? (
            <div className="p-8">
              <EmptyState
                icon={AlertTriangle}
                title="No complaints found"
                description={
                  search || status || priority
                    ? "No complaints match these filters. Try widening them."
                    : "Nothing has been raised against you yet — complaints appear here as your team files them."
                }
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-left">
                <thead>
                  <tr className="text-[10px] sm:text-label-sm uppercase tracking-wide text-gray-700 border-b-2 border-emerald-200/60 bg-emerald-50/50">
                    <th className="px-3 sm:px-4 py-2 sm:py-3.5 font-bold">Complaint</th>
                    {isChain && (
                      <th className="px-3 sm:px-4 py-2 sm:py-3.5 font-bold hidden md:table-cell">Branch</th>
                    )}
                    <th className="px-3 sm:px-4 py-2 sm:py-3.5 font-bold hidden xs:table-cell">Raised by</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3.5 font-bold hidden sm:table-cell">Assignee</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3.5 font-bold">Priority</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3.5 font-bold hidden xs:table-cell">Status</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3.5 font-bold">Resolved</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3.5 font-bold hidden md:table-cell">Updated</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-emerald-200/50">
                  {items.map((c) => (
                    <tr
                      key={c._id}
                      className="cursor-pointer text-xs sm:text-body-sm text-gray-800 hover:bg-emerald-100/40 transition-colors duration-200 group"
                    >
                      <td className="px-3 sm:px-4 py-2 sm:py-3.5">
                        <Link to={`/complaints/${c._id}`} className="block group-hover:text-emerald-800 transition-colors">
                          <p className="font-semibold text-gray-900 group-hover:text-emerald-800 transition-colors text-xs sm:text-sm line-clamp-1">
                            {c.title}
                          </p>
                          <p className="text-[10px] sm:text-body-sm text-gray-600 line-clamp-1">
                            {c.complaintId} · {c.category}
                          </p>
                        </Link>
                      </td>
                      {isChain && (
                        <td className="px-3 sm:px-4 py-2 sm:py-3.5 hidden md:table-cell">
                          <div className="flex items-center gap-1 sm:gap-2">
                            <Store size={12} className="text-gray-500" />
                            <span className="text-gray-700 font-medium text-xs">
                              {refLabel(c.partner, "—")}
                            </span>
                          </div>
                        </td>
                      )}
                      <td className="px-3 sm:px-4 py-2 sm:py-3.5 hidden xs:table-cell">
                        <div className="flex items-center gap-1 sm:gap-2">
                          <User size={12} className="text-gray-500 hidden sm:block" />
                          <span className="text-gray-700 font-medium text-xs">{c.raiser?.name ?? "—"}</span>
                        </div>
                      </td>
                      <td className="px-3 sm:px-4 py-2 sm:py-3.5 hidden sm:table-cell">
                        <div className="flex items-center gap-1 sm:gap-2">
                          <Users size={12} className="text-gray-500" />
                          <span className="text-gray-700 font-medium text-xs">{c.assignee?.name ?? "Unassigned"}</span>
                        </div>
                      </td>
                      <td className="px-3 sm:px-4 py-2 sm:py-3.5">
                        <PriorityChip priority={c.priority} />
                      </td>
                      <td className="px-3 sm:px-4 py-2 sm:py-3.5 hidden xs:table-cell">
                        <StatusChip status={c.status} />
                      </td>
                      <td className="px-3 sm:px-4 py-2 sm:py-3.5">
                        {c.status === "RESOLVED" ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
                            <CheckCircle2 size={11} /> Resolved
                          </span>
                        ) : c.status === "REOPENED" ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2.5 py-1 text-[11px] font-semibold text-orange-700">
                            <CircleDot size={11} /> Reopened
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-semibold text-gray-500">
                            <CircleDot size={11} /> Open
                          </span>
                        )}
                      </td>
                      <td className="px-3 sm:px-4 py-2 sm:py-3.5 hidden md:table-cell">
                        <div className="flex items-center gap-1 text-gray-600">
                          <Clock size={12} className="text-gray-500 hidden sm:block" />
                          <span className="font-medium text-xs">{formatDate(c.updatedAt)}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && items.length > 0 && (
            <div className="px-3 sm:px-4 border-t-2 border-emerald-200/60 pt-3 sm:pt-4 bg-emerald-50/30 rounded-b-xl">
              <Pagination
                page={pagination.page}
                pages={pagination.pages}
                total={pagination.total}
                limit={pagination.limit}
                onPageChange={setPage}
              />
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}